import { useEffect } from "react";

import TextAreaWithCounter from "@hl/base-components/lib/inputs/TextAreaWithCounter";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { Box, Button, Modal, Space, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { isEmpty } from "lodash";

import { getCollectionProperties } from "~/features/tokens/sections/ManageToken/ManageToken";
import useUpdateMintVectorOnChain from "~/features/tokens/sections/ManageToken/modals/EditDetails/useEditCollectionDetails";
import { GetManageTokenCollectionQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

/* eslint-disable  @typescript-eslint/no-explicit-any */
type Props = {
  collection: GetManageTokenCollectionQuery["getCollection"];
  isOpened: boolean;
  onClose: () => void;
  refetch?: () => void;
};

interface EditDetailsFormValues {
  name: string;
  description: string;
}

export const EditDetails = ({
  isOpened,
  onClose,
  collection,
  refetch,
}: Props) => {
  const collectionProperties = getCollectionProperties(collection);

  const closeModal = () => {
    refetch?.();
    form.reset();
    onClose();
  };

  const {
    editDetails,
    buttonLoading,
    buttonLabel,
    isOnChainTransaction,
    error,
  } = useUpdateMintVectorOnChain({ collection, onFinish: closeModal });

  const form = useForm<EditDetailsFormValues>({
    initialValues: {
      name: collectionProperties.name || "",
      description: collectionProperties.description || "",
    },
    validate: (values) => ({
      name: isEmpty(values.name)
        ? "Name is required"
        : values.name.length < 3 || values.name.length > 80
        ? "Name must have between 3 and 80 chars"
        : null,
      description: isEmpty(values.description)
        ? "Description is required"
        : values.description.length < 3 || values.description.length > 10000
        ? "Description must have between 3 and 10000 chars"
        : null,
    }),
  });

  const onSubmit = form.onSubmit((values) => editDetails({ ...values }));

  useEffect(() => {
    form.validate();
  }, [form.values]);

  return (
    <Modal
      size="lg"
      opened={isOpened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title="Edit Details"
    >
      <Box mx="auto">
        <form onSubmit={onSubmit}>
          <TextInput
            placeholder=""
            spellCheck={false}
            label="Name"
            {...form.getInputProps("name")}
          />
          <Space h="lg" />
          <TextAreaWithCounter
            placeholder=""
            label="Description"
            minRows={3}
            {...form.getInputProps("description")}
            autosize
            maxLength={10000}
            spellCheck={false}
            characterCounter
          />
          <ModalSection>
            <Button
              type="submit"
              size="xl"
              fullWidth
              loading={buttonLoading}
              disabled={!form.isValid()}
            >
              {buttonLabel || "Save changes"}
            </Button>
            {error && (
              <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
                {error}
              </Text>
            )}
            {isOnChainTransaction && (
              <Text size="xs" align="center" mt={8}>
                This action requires a transaction
              </Text>
            )}
          </ModalSection>
        </form>
      </Box>
    </Modal>
  );
};
